import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { BookOpen } from 'lucide-react';
import { adminApi, type OrgSummary } from '@/lib/api/admin';
import { curriculumApi, type TemplateListItem } from '@/lib/api/curriculum';
import { toast } from 'sonner';
import { DatePicker } from '@/components/ui/date-picker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export function CreateBatchModal({
  orgs,
  onClose,
  onCreated,
}: {
  orgs: OrgSummary[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const navigate = useNavigate();
  const [step, setStep]             = useState<1 | 2>(1);
  const [orgId, setOrgId]           = useState('');
  const [name, setName]             = useState('');
  const [startDate, setStartDate]   = useState('');
  const [endDate, setEndDate]       = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [templates, setTemplates]                   = useState<TemplateListItem[]>([]);
  const [templatesLoading, setTemplatesLoading]     = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | 'scratch' | ''>('');
  const [createdBatchId, setCreatedBatchId]         = useState('');

  useEffect(() => {
    if (step !== 2) return;
    setTemplatesLoading(true);
    curriculumApi.listTemplates()
      .then(({ data }) => setTemplates((data ?? []).filter((t) => t.status === 'published')))
      .catch(() => toast.error('Failed to load templates'))
      .finally(() => setTemplatesLoading(false));
  }, [step]);

  const handleNext = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const batch = await adminApi.createBatch({
        org_id: orgId,
        name,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });
      setCreatedBatchId(batch.id);
      setStep(2);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to create batch');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinish = async () => {
    if (!selectedTemplateId) {
      toast.success('Batch created');
      onCreated();
      return;
    }
    if (selectedTemplateId === 'scratch') {
      toast.success('Batch created');
      onCreated();
      navigate({ to: '/admin/batches/$batchId/curriculum', params: { batchId: createdBatchId }, search: { topicId: undefined } });
      return;
    }
    setSubmitting(true);
    try {
      await curriculumApi.forkTemplate(selectedTemplateId, createdBatchId);
      toast.success('Batch created with curriculum');
      onCreated();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to assign curriculum');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-w-md p-0"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="border-b border-border px-5 py-4">
          <DialogTitle className="text-[15px]">
            {step === 1 ? 'New Batch' : 'Assign Curriculum'}
          </DialogTitle>
          <p className="text-[11px] text-muted-foreground">Step {step} of 2</p>
        </DialogHeader>

        {step === 1 && (
          <form onSubmit={handleNext}>
            <div className="space-y-4 px-5 py-5">
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-foreground">
                  Organization
                </label>
                <Select value={orgId} onValueChange={setOrgId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select org…" />
                  </SelectTrigger>
                  <SelectContent>
                    {orgs.map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-foreground">
                  Batch name
                </label>
                <Input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. MERN Stack — May 2026"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-foreground">Start date</label>
                  <DatePicker value={startDate} onChange={setStartDate} />
                </div>
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-foreground">End date</label>
                  <DatePicker value={endDate} onChange={setEndDate} />
                </div>
              </div>
            </div>
            <DialogFooter className="border-t border-border px-5 py-4">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={submitting || !orgId || !name}>
                {submitting ? 'Creating…' : 'Next →'}
              </Button>
            </DialogFooter>
          </form>
        )}

        {step === 2 && (
          <div>
            <div className="space-y-2 px-5 py-5">
              {templatesLoading ? (
                <p className="text-[13px] text-muted-foreground">Loading templates…</p>
              ) : templates.length === 0 ? (
                <p className="text-[13px] text-muted-foreground">No published templates available.</p>
              ) : (
                templates.map((t) => (
                  <label
                    key={t.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-3 transition-colors ${
                      selectedTemplateId === t.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/30'
                    }`}
                  >
                    <input
                      type="radio"
                      name="template"
                      value={t.id}
                      checked={selectedTemplateId === t.id}
                      onChange={() => setSelectedTemplateId(t.id)}
                      className="mt-0.5 accent-primary"
                    />
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-foreground">{t.title}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {t.module_count} modules · {t.topic_count} topics · {t.total_hours.toFixed(0)}h
                        {t.usage_count > 0 && ` · used by ${t.usage_count} batch${t.usage_count !== 1 ? 'es' : ''}`}
                      </p>
                    </div>
                  </label>
                ))
              )}

              <label
                className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-3 transition-colors ${
                  selectedTemplateId === 'scratch' ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/30'
                }`}
              >
                <input
                  type="radio"
                  name="template"
                  value="scratch"
                  checked={selectedTemplateId === 'scratch'}
                  onChange={() => setSelectedTemplateId('scratch')}
                  className="mt-0.5 accent-primary"
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
                    <p className="text-[13px] font-medium text-foreground">Start from scratch</p>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Open the curriculum editor with a blank slate</p>
                </div>
              </label>
            </div>

            <DialogFooter className="justify-between border-t border-border px-5 py-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => { onCreated(); toast.success('Batch created'); }}
              >
                Skip
              </Button>
              <Button
                type="button"
                onClick={handleFinish}
                disabled={submitting || !selectedTemplateId}
              >
                {submitting
                  ? 'Assigning…'
                  : selectedTemplateId === 'scratch'
                  ? 'Open editor →'
                  : 'Assign & finish'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
